import type { ComponentPublicInstanceConstructor } from "vue/types/v3-component-public-instance";
import { pushDialog, removeDialog } from "./dialogHostStore";

type ComponentProps<D extends ComponentPublicInstanceConstructor> = Omit<
  InstanceType<D>["$props"],
  "value" | "visible"
>;

export interface DialogResult<T> {
  wasSuccessful: boolean;
  value: T | null;
}

/**
 * Queues a dialog to be displayed by the app-level dialog host - can be executed
 * from any async handler.
 *
 * The dialog is rendered by `ModalDialogStack`, so it inherits vuetify, i18n,
 * router and the global error handler without any manual injection.
 */
export function showModalDialog<
  T,
  D extends ComponentPublicInstanceConstructor,
>(
  dialog: ComponentPublicInstanceConstructor,
  options: {
    props: ComponentProps<D>;
    attrs?: Record<string, any>;
  }
): Promise<DialogResult<T>> {
  const { props: propsData, attrs } = options;

  return new Promise<DialogResult<T>>((resolve) => {
    const id = pushDialog({
      component: dialog,
      props: {
        value: true,
        visible: true,
        ...propsData,
      },
      attrs: attrs ?? {},
      onAccept: (value: T) => {
        removeDialog(id);
        resolve({
          wasSuccessful: true,
          value,
        });
      },
      onCancel: () => {
        removeDialog(id);
        resolve({
          wasSuccessful: false,
          value: null,
        });
      },
    });
  });
}
